import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  DISSOLVE_CURVE,
  FOLD_CURVE,
  ICON_IN_CURVE,
  MORPH_CURVE,
  MOTION as CORE_MOTION,
  MOVE_CURVE,
  SETTLE_CURVE,
  cssEase,
} from "@libs/checkout-core";

/**
 * One motion, everywhere.
 *
 * Every block that appears, leaves or changes face on the checkout does it
 * through this file, with this one timing, so the page never has two speeds.
 * A partner cannot override it: motion is part of the checkout's manner, not
 * its palette. The numbers are the core's (`@libs/checkout-core` motion.ts),
 * shared with the native renderer; this file writes them as CSS.
 */
export const MOTION = {
  durationMs: CORE_MOTION.durationMs,
  ease: cssEase(CORE_MOTION.curve),
  /** How far a block travels while fading in. */
  rise: `${CORE_MOTION.risePx}px`,
} as const;

/** The fold: a deliberate collapse, easing in and out, not a snap. */
export const FOLD_EASE = cssEase(FOLD_CURVE);
/** Something already on screen moving to a new place: in and out. */
export const MOVE_EASE = cssEase(MOVE_CURVE);
/** Fast out, long soft landing: the status, the success rows, a glide. */
export const SETTLE_EASE = cssEase(SETTLE_CURVE);
export const MORPH_EASE = cssEase(MORPH_CURVE);
export const ICON_IN_EASE = cssEase(ICON_IN_CURVE);
export const DISSOLVE_EASE = cssEase(DISSOLVE_CURVE);

const transition = `opacity ${MOTION.durationMs}ms ${MOTION.ease}, transform ${MOTION.durationMs}ms ${MOTION.ease}`;

/**
 * Mounts its child with a fade-and-rise, and keeps it on screen long enough
 * to fade out when `show` turns false. Layout collapses only once the block
 * has gone, so nothing under it jumps mid-fade.
 */
export function Reveal({
  show,
  children,
  style,
}: {
  show: boolean;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const [mounted, setMounted] = useState(show);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      setMounted(true);
      const frame = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), MOTION.durationMs);
    return () => window.clearTimeout(timer);
  }, [show]);
  if (!mounted) return null;
  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(${MOTION.rise})`,
        transition,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * One slot, several faces. When `face` changes the old face fades out and
 * the new one fades in, in place; the slot never shows two at once and never
 * shows nothing for longer than the fade.
 */
export function Crossfade({
  face,
  children,
  style,
}: {
  face: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  // What was last on screen, so it can be kept while it fades out.
  const last = useRef<{ face: string; children: ReactNode }>({
    face,
    children,
  });
  const [outgoing, setOutgoing] = useState<{
    face: string;
    children: ReactNode;
  } | null>(null);
  const [entering, setEntering] = useState(false);
  const changed = last.current.face !== face;
  useEffect(() => {
    if (!changed) {
      last.current = { face, children };
      return;
    }
    setOutgoing(last.current);
    last.current = { face, children };
    const timer = window.setTimeout(() => {
      setOutgoing(null);
      setEntering(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setEntering(false)),
      );
    }, MOTION.durationMs);
    return () => window.clearTimeout(timer);
    // Deliberately keyed on the face alone: fresh children of the same face
    // swap in place without a fade.
  }, [face]);
  const content = outgoing
    ? outgoing.children
    : changed
      ? last.current.children
      : children;
  const visible = !outgoing && !changed && !entering;
  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(${MOTION.rise})`,
        transition,
        ...style,
      }}
    >
      {content}
    </div>
  );
}
